import React, { FC, useState, useRef } from 'react';
import { renderParagraphs } from '@lib/utils';
import { GroupedFAQST, FAQGroupT } from '@factories/RowFactory/components/GroupedFAQS/types';
import { SimpleFAQT } from '@components/Organisms/SimpleFAQs/types';
import {
  StyledGroupedFAQS,
  StyledFAQGroup,
  StyledFAQItem,
} from '@factories/RowFactory/components/GroupedFAQS/styled/StyledGroupedFAQS';

const FAQItem: FC<{
  faq: SimpleFAQT;
  index: number;
  isActive: boolean;
  onClick: () => void;
}> = ({ faq, index, isActive, onClick }) => {
  const contentRef = useRef<HTMLDivElement>(null);

  if (!faq.display) return null;

  return (
    <StyledFAQItem isOpen={isActive}>
      <button onClick={onClick} aria-expanded={isActive} aria-controls={`faq-answer-${index}`}>
        <h3>{faq.question}</h3>
      </button>
      <div
        id={`faq-answer-${index}`}
        ref={contentRef}
        className="answer"
        style={{
          maxHeight: isActive ? `${contentRef.current?.scrollHeight}px` : '0px',
        }}
      >
        {renderParagraphs({ body: faq.answer, hasMarkdown: true })}
      </div>
    </StyledFAQItem>
  );
};

const FAQGroup: FC<{
  group: FAQGroupT;
  activeIndexes: Record<string, number | null>;
  setActiveIndex: (groupId: string, index: number | null) => void;
}> = ({ group, activeIndexes, setActiveIndex }) => {
  if (!group.display) return null;

  const groupId = `group-${group.title.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <StyledFAQGroup>
      <h3>{group.title}</h3>
      <div className="faq-items">
        {group.items.map((item, index) => (
          <FAQItem
            key={`${groupId}-faq-${index}`}
            index={index}
            faq={item}
            isActive={activeIndexes[groupId] === index}
            onClick={() => setActiveIndex(groupId, activeIndexes[groupId] === index ? null : index)}
          />
        ))}
      </div>
    </StyledFAQGroup>
  );
};

export const GroupedFAQS: FC<GroupedFAQST> = ({
  display,
  title,
  subTitle,
  description,
  FAQGroups,
}) => {
  const [activeIndexes, setActiveIndexes] = useState<Record<string, number | null>>({});

  if (!display) return null;

  const setActiveIndex = (groupId: string, index: number | null) => {
    setActiveIndexes((prev) => ({
      ...prev,
      [groupId]: index,
    }));
  };

  return (
    <StyledGroupedFAQS>
      <h2>{title}</h2>
      {subTitle && <div className="subtitle">{subTitle}</div>}
      {description && (
        <div className="description">
          {renderParagraphs({ body: description, hasMarkdown: true })}
        </div>
      )}
      <div className="md:mx-auto md:container">
        <div className="groups-wrapper">
          {FAQGroups.map((group, index) => (
            <FAQGroup
              key={`faq-group-${index}`}
              group={group}
              activeIndexes={activeIndexes}
              setActiveIndex={setActiveIndex}
            />
          ))}
        </div>
      </div>
    </StyledGroupedFAQS>
  );
};

export default GroupedFAQS;
